import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Radio, Calendar, PlayCircle, ExternalLink, Gift, Bell,
  Share2, Copy, Check, MessageSquare, Send, Loader2, Crown, Building2, ArrowLeft,
} from "lucide-react";
import SeoMeta from "@/components/SeoMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
const publicClient = axios.create({ withCredentials: false });

const STATUS_LABEL = {
  scheduled: "Scheduled",
  live: "LIVE NOW",
  replay: "Replay Available",
  ended: "Ended",
};

const STATUS_BAR = {
  scheduled: "from-[#3B82F6]/15 to-transparent",
  live: "from-[#EF4444]/25 to-transparent",
  replay: "from-[#10B981]/15 to-transparent",
  ended: "from-white/5 to-transparent",
};

const PLATFORM_LABEL = {
  youtube: "YouTube Live", facebook: "Facebook Live", zoom: "Zoom",
  streamyard: "StreamYard", vimeo: "Vimeo", tiktok: "TikTok Live",
  twitch: "Twitch", kick: "Kick", linkedin: "LinkedIn Live",
  rumble: "Rumble", amazon: "Amazon Live", whatnot: "Whatnot",
  instagram: "Instagram Live",
  custom: "Live Stream",
};

function youtubeEmbed(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}?autoplay=0`;
    }
    if (u.hostname === "youtu.be") {
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}?autoplay=0`;
    }
  } catch (_) {}
  return null;
}
function vimeoEmbed(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean).pop();
      if (id) return `https://player.vimeo.com/video/${id}`;
    }
  } catch (_) {}
  return null;
}

function pickEmbedSrc(ev) {
  const url = ev.status === "replay" && ev.replay_url ? ev.replay_url : ev.stream_url;
  if (!url) return null;
  if (ev.platform === "youtube") return youtubeEmbed(url);
  if (ev.platform === "vimeo") return vimeoEmbed(url);
  return null;
}

export default function LiveEventPage() {
  const { slug, eventSlug } = useParams();
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const viewTrackedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    publicClient
      .get(`${API}/live-events/${slug}/${eventSlug}`, { headers: { Authorization: "" } })
      .then((r) => { if (!cancelled) setEv(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.status === 404 ? "Event not found" : "Failed to load"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, eventSlug]);

  useEffect(() => {
    if (ev && !viewTrackedRef.current) {
      viewTrackedRef.current = true;
      track(ev.id, "live_event_view");
    }
  }, [ev]);

  if (loading) {
    return <div className="min-h-screen bg-[#070B16] pt-32 px-4"><div className="max-w-5xl mx-auto h-96 bg-white/5 rounded-3xl animate-pulse" /></div>;
  }
  if (error || !ev) {
    return (
      <div className="min-h-screen bg-[#070B16] pt-32 px-4 text-center">
        <p className="text-white font-semibold">{error || "Event not found"}</p>
        <Link to="/" className="text-[#FBBF24] text-sm mt-3 inline-block">Back home</Link>
      </div>
    );
  }

  const embedSrc = pickEmbedSrc(ev);
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&format=png&margin=10&data=${encodeURIComponent(typeof window !== "undefined" ? window.location.href : "")}`;
  const watchUrl = ev.status === "replay" && ev.replay_url ? ev.replay_url : ev.stream_url;

  return (
    <div className="min-h-screen bg-[#070B16] text-white pt-24 pb-24" data-testid="live-event-page">
      <SeoMeta
        title={`${ev.title} — ${ev.listing_name || "LocalBest.ai"}`}
        description={(ev.description || "Live event on LocalBest.ai").slice(0, 200)}
        ogImage={ev.image_url}
      />
      <div className={`bg-gradient-to-b ${STATUS_BAR[ev.status] || ""}`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <Link to={`/business/${ev.listing_slug}`} className="inline-flex items-center gap-1.5 text-xs text-[#94A3B8] hover:text-white mb-4">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to {ev.listing_name || ev.listing_slug}
          </Link>

          {/* Header */}
          <div className="flex items-center gap-4 mb-5 flex-wrap">
            {ev.listing_logo ? (
              <img src={ev.listing_logo} alt="" className="w-14 h-14 rounded-xl object-cover bg-white/10" />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#94A3B8]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs text-[#94A3B8]">{ev.listing_name}</p>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Outfit'] text-white leading-tight">{ev.title}</h1>
              <div className="mt-2 flex items-center gap-2 flex-wrap text-xs text-[#94A3B8]">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ${
                  ev.status === "live" ? "bg-[#EF4444]/20 text-[#EF4444] animate-pulse" :
                  ev.status === "replay" ? "bg-[#10B981]/20 text-[#10B981]" :
                  ev.status === "scheduled" ? "bg-[#3B82F6]/20 text-[#3B82F6]" :
                  "bg-white/10 text-[#94A3B8]"
                }`}>
                  {ev.status === "live" && <Radio className="w-3 h-3" />} {STATUS_LABEL[ev.status] || ev.status}
                </span>
                <span>{PLATFORM_LABEL[ev.platform] || ev.platform}</span>
                {ev.starts_at && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(ev.starts_at).toLocaleString()}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_320px] gap-6 mt-6">
        {/* Main column */}
        <div className="space-y-5 min-w-0">
          {/* Video / embed */}
          <div className="aspect-video w-full rounded-2xl overflow-hidden bg-black border border-white/10" data-testid="live-event-video">
            {ev.embed_code ? (
              <div dangerouslySetInnerHTML={{ __html: ev.embed_code }} className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full" />
            ) : embedSrc ? (
              <iframe
                src={embedSrc}
                title={ev.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                onLoad={() => track(ev.id, ev.status === "replay" ? "live_replay_view" : "live_watch_click")}
              />
            ) : watchUrl ? (
              <a href={watchUrl} target="_blank" rel="noopener noreferrer" onClick={() => track(ev.id, ev.status === "replay" ? "live_replay_view" : "live_watch_click")} className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0F172A] to-[#070B16] hover:from-[#1E293B] transition" data-testid="external-watch-btn">
                <div className="text-center">
                  <PlayCircle className="w-16 h-16 text-[#EF4444] mx-auto mb-3" />
                  <p className="text-white font-semibold">Watch on {PLATFORM_LABEL[ev.platform] || "external platform"}</p>
                  <p className="text-xs text-[#94A3B8] mt-1 truncate max-w-[80%] mx-auto">{watchUrl}</p>
                </div>
              </a>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#94A3B8]">
                <p>Stream link not provided yet</p>
              </div>
            )}
          </div>

          {/* Countdown */}
          {ev.status === "scheduled" && ev.starts_at && (
            <CountdownBar to={ev.starts_at} />
          )}

          {/* CTAs */}
          <div className="flex flex-wrap gap-2" data-testid="event-cta-row">
            {ev.cta_label && ev.cta_url && (
              <a href={ev.cta_url} target="_blank" rel="noopener noreferrer" onClick={() => track(ev.id, "live_cta_click")} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#FBBF24] to-[#EC4899] text-white text-sm font-semibold" data-testid="event-cta-btn">
                {ev.cta_label} <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
            {ev.offer_url && (
              <a href={ev.offer_url} target="_blank" rel="noopener noreferrer" onClick={() => track(ev.id, "live_offer_click")} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#10B981]/15 border border-[#10B981]/30 text-[#10B981] text-sm font-semibold" data-testid="event-offer-btn">
                <Gift className="w-4 h-4" /> Claim Offer
              </a>
            )}
            <Link to={`/business/${ev.listing_slug}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-semibold">
              <Building2 className="w-4 h-4" /> Contact {ev.listing_name || "this business"}
            </Link>
            <ShareButton url={typeof window !== "undefined" ? window.location.href : ""} title={ev.title} eventId={ev.id} />
          </div>

          {/* Offer block */}
          {ev.offer_text && (
            <div className="rounded-2xl border border-[#10B981]/30 bg-gradient-to-br from-[#10B981]/10 to-transparent p-5">
              <div className="flex items-start gap-3">
                <Gift className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-white font-semibold">{ev.offer_text}</p>
                  {ev.offer_code && (
                    <p className="mt-1.5 text-xs text-[#94A3B8]">
                      Code: <span className="font-mono px-2 py-0.5 rounded bg-white/5 text-[#FBBF24]">{ev.offer_code}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          {ev.description && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <p className="text-[#CBD5E1] text-sm leading-relaxed whitespace-pre-line">{ev.description}</p>
            </div>
          )}

          {/* Q&A */}
          {ev.question_form_enabled && <QuestionForm eventId={ev.id} />}
        </div>

        {/* Side column */}
        <aside className="space-y-5">
          {/* Lead capture */}
          {ev.lead_form_enabled && <LeadForm eventId={ev.id} status={ev.status} />}

          {/* QR */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-center" data-testid="event-qr">
            <p className="text-xs uppercase tracking-wider text-[#94A3B8] mb-3">Share this event</p>
            <div className="inline-block bg-white p-2 rounded-xl">
              <img src={qrUrl} alt="Event QR" className="w-32 h-32" />
            </div>
            <p className="mt-3 text-[11px] text-[#64748B]">Scan to open this event page</p>
          </div>

          <Link
            to="/onlybest"
            className="block rounded-2xl border border-[#FBBF24]/30 bg-gradient-to-br from-[#FBBF24]/10 to-transparent p-4 hover:border-[#FBBF24]/60 transition"
          >
            <p className="text-white font-semibold text-sm flex items-center gap-1.5"><Crown className="w-4 h-4 text-[#FBBF24]" /> Promote events like this</p>
            <p className="text-xs text-[#94A3B8] mt-1">Become an OnlyBest Creator and earn by sharing live events from local businesses.</p>
          </Link>
        </aside>
      </div>
    </div>
  );
}

function CountdownBar({ to }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, new Date(to).getTime() - now);
  const s = Math.floor(diff / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-2xl border border-[#3B82F6]/30 bg-gradient-to-r from-[#3B82F6]/10 via-[#0F172A] to-[#7C3AED]/10 p-4 sm:p-5 flex items-center justify-center gap-4 sm:gap-6 flex-wrap" data-testid="event-countdown-bar">
      {[{ v: d, l: "Days" }, { v: h, l: "Hrs" }, { v: m, l: "Min" }, { v: ss, l: "Sec" }].map((b) => (
        <div key={b.l} className="text-center min-w-[44px]">
          <p className="text-2xl sm:text-3xl font-bold text-white tabular-nums">{String(b.v).padStart(2, "0")}</p>
          <p className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{b.l}</p>
        </div>
      ))}
    </motion.div>
  );
}

function ShareButton({ url, title, eventId }) {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    track(eventId, "live_share_click");
    if (navigator.share) {
      try { await navigator.share({ title, url }); return; } catch (_) {}
    }
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) {}
  };
  return (
    <button onClick={share} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-semibold" data-testid="event-share-btn">
      {copied ? <Check className="w-4 h-4 text-[#10B981]" /> : <Share2 className="w-4 h-4" />} Share Event
    </button>
  );
}

function QuestionForm({ eventId }) {
  const [form, setForm] = useState({ name: "", email: "", question: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.question.trim()) return;
    setSending(true);
    try {
      await publicClient.post(`${API}/live-events/${eventId}/question`, form, { headers: { Authorization: "" } });
      setSent(true);
      setForm({ name: "", email: "", question: "" });
      toast.success("Question submitted!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSending(false); }
  };
  if (sent) {
    return (
      <div className="rounded-2xl border border-[#10B981]/30 bg-[#10B981]/5 p-5 text-center" data-testid="question-form-sent">
        <Check className="w-6 h-6 text-[#10B981] mx-auto mb-2" />
        <p className="text-white font-semibold text-sm">Thanks — your question was submitted!</p>
        <button onClick={() => setSent(false)} className="text-xs text-[#FBBF24] mt-2">Ask another</button>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 space-y-3" data-testid="question-form">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-[#FBBF24]" />
        <p className="text-white font-semibold text-sm">Ask a Question</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name (optional)" className={inputCls} />
        <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" placeholder="Email (optional)" className={inputCls} />
      </div>
      <textarea required value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} rows={3} placeholder="What would you like to ask?" className={inputCls} data-testid="question-input" />
      <button disabled={sending} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#FBBF24] to-[#EC4899] text-white text-sm font-semibold disabled:opacity-60" data-testid="question-submit">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit
      </button>
    </form>
  );
}

function LeadForm({ eventId, status }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name + email required"); return; }
    setSending(true);
    try {
      await publicClient.post(`${API}/live-events/${eventId}/register`, form, { headers: { Authorization: "" } });
      setSent(true);
      toast.success("You're on the list!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSending(false); }
  };
  if (sent) {
    return (
      <div className="rounded-2xl border border-[#10B981]/30 bg-[#10B981]/5 p-5 text-center" data-testid="lead-form-sent">
        <Check className="w-6 h-6 text-[#10B981] mx-auto mb-2" />
        <p className="text-white font-semibold text-sm">You're registered!</p>
        <p className="text-xs text-[#94A3B8] mt-1">We'll remind you before the event.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="rounded-2xl border border-[#FBBF24]/30 bg-gradient-to-br from-[#FBBF24]/10 to-transparent p-5 space-y-3" data-testid="lead-form">
      <p className="text-white font-semibold text-sm flex items-center gap-1.5">
        <Bell className="w-4 h-4 text-[#FBBF24]" /> {status === "scheduled" ? "Get a Reminder" : "Register"}
      </p>
      <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className={inputCls} />
      <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={inputCls} />
      <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className={inputCls} />
      <button disabled={sending} className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#FBBF24] to-[#EC4899] text-white text-sm font-semibold disabled:opacity-60" data-testid="lead-submit">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} {status === "scheduled" ? "Get Reminder" : "Register"}
      </button>
    </form>
  );
}

const inputCls = "w-full px-3 py-2 bg-[#070B16] border border-white/10 rounded-lg text-white text-sm placeholder:text-[#475569] focus:outline-none focus:border-[#FBBF24]/60";

function track(eventId, name, extra = {}) {
  if (!eventId) return;
  publicClient.post(`${API}/live-events/${eventId}/track`, { event_name: name, ...extra }, { headers: { Authorization: "" } }).catch(() => {});
}
